import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AutoCompleteModule } from '@openng/optimus-ui/autocomplete';
import type { AutoCompleteCompleteEvent, AutoCompleteSelectEvent } from '@openng/optimus-ui/autocomplete';
import {
  ALL_CHANNELS_FILTER,
  ChannelFilterChip,
  ChannelSearchMatch,
} from '../../../../models/template/channel-search-result.interface';

/**
 * Region 1 of the mockup: topbar (back, channel + template name, search,
 * Duplicate/Translate/Export/Delete, Connect to Campaign, Save to Responsys).
 * Purely presentational - every action is an @Output the orchestrator wires
 * straight to the existing BaseTemplateEditorComponent methods.
 *
 * RCA-487: the mockup's 🔍 "Search or create campaign" popover (channel
 * filter chips + cross-channel results) is additive here, next to the
 * existing template-name field - it does not replace that field's
 * same-channel search/create/open behavior.
 */
@Component({
  selector: 'app-navbar-v2',
  imports: [CommonModule, FormsModule, AutoCompleteModule],
  templateUrl: './navbar-v2.component.html',
  styleUrl: './navbar-v2.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NavbarV2Component {
  @Input() channelLabel = '';
  @Input() templateName: string | null = null;
  @Input() suggestions: any[] | undefined;
  @Input() namePlaceholder = '';
  @Input() emptyMessage = '';
  @Input() idcustom = '';

  @Input() showTranslateButton = true;
  @Input() showResponsysButton = false;
  @Input() showConnectCampaign = false;

  @Input() deleteDisabled = false;
  @Input() exportDisabled = false;
  @Input() duplicateDisabled = false;
  @Input() translateDisabled = false;
  @Input() connectCampaignDisabled = false;
  @Input() saveResponsysDisabled = false;

  // RCA-487: cross-channel campaign search popover.
  @Input() channelFilterChips: ChannelFilterChip[] = [];
  @Input() activeChannelFilter: string = ALL_CHANNELS_FILTER;
  @Input() channelSearchMatches: ChannelSearchMatch[] = [];
  @Input() channelSearchLoading = false;

  @Output() back = new EventEmitter<void>();
  @Output() templateNameChange = new EventEmitter<string | null>();
  @Output() searchTemplate = new EventEmitter<AutoCompleteCompleteEvent>();
  @Output() selectTemplate = new EventEmitter<AutoCompleteSelectEvent>();
  @Output() deleteClick = new EventEmitter<void>();
  @Output() exportClick = new EventEmitter<void>();
  @Output() duplicateClick = new EventEmitter<void>();
  @Output() translateClick = new EventEmitter<void>();
  @Output() connectCampaignClick = new EventEmitter<void>();
  @Output() saveResponsysClick = new EventEmitter<void>();

  @Output() channelSearchOpened = new EventEmitter<void>();
  @Output() channelSearchTermChange = new EventEmitter<string>();
  @Output() channelSearchFilterChange = new EventEmitter<string>();
  @Output() channelSearchResultClick = new EventEmitter<ChannelSearchMatch>();

  protected channelSearchOpen = false;
  protected channelSearchTerm = '';

  protected copyTemplateName(): void {
    if (this.templateName) {
      navigator.clipboard.writeText(this.templateName);
    }
  }

  protected toggleChannelSearch(): void {
    this.channelSearchOpen = !this.channelSearchOpen;
    if (this.channelSearchOpen) {
      this.channelSearchOpened.emit();
    }
  }

  protected closeChannelSearch(): void {
    this.channelSearchOpen = false;
  }

  protected onChannelSearchTermChange(value: string): void {
    this.channelSearchTerm = value;
    this.channelSearchTermChange.emit(value);
  }

  protected clearChannelSearchTerm(): void {
    this.onChannelSearchTermChange('');
  }

  protected selectChannelFilter(key: string): void {
    this.channelSearchFilterChange.emit(key);
  }

  protected openChannelSearchMatch(match: ChannelSearchMatch): void {
    this.channelSearchResultClick.emit(match);
    this.closeChannelSearch();
  }
}
